import React, { useState, useEffect, useRef, useContext } from 'react';
import { Table } from 'reactstrap';
import { AppContext } from '../application/provider';
import cobranzasService from '../axios_services/finanzascobranzas.service';
import ModalFinanzasCobranzas from '../components/Modal/ModalFinanzasCobranzas';
import Loader from '../components/Loader/Loader';
import { ButtonSearch, ButtonUpdate, ButtonDelete, ButtonInsert, ButtonExcel } from '../components/Buttons/Buttons';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// ─── Catálogos (etiquetas para mostrar en tabla) ──────────────────────────────
const LABEL_TIPO_COMPROBANTE = { 1: 'Factura', 2: 'Boleta', 3: 'Rec. Honorarios', 4: 'Nota Crédito', 5: 'Liquidación' };
const LABEL_TIPO_INGRESO = { 1: 'Servicio', 2: 'Producto', 3: 'Alquiler', 4: 'Consultoría', 5: 'Otro' };
const LABEL_ESTADO_PAGO = { 0: 'Pendiente', 1: 'Pago Parcial', 2: 'Pagado', 3: 'Anulado' };
const LABEL_ESTADO_CONT = { 0: 'Pend. Revisión', 1: 'Revisado', 2: 'Contabilizado' };

const COLOR_ESTADO_PAGO = {
    0: { background: '#fff3cd', color: '#856404' },
    1: { background: '#cce5ff', color: '#004085' },
    2: { background: '#d4edda', color: '#155724' },
    3: { background: '#f8d7da', color: '#721c24' },
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${day}/${m}/${y}`;
};

const formatMonto = (v) =>
    v !== null && v !== undefined && v !== ''
        ? `S/ ${parseFloat(v).toFixed(2)}`
        : '—';

// ─── Component ────────────────────────────────────────────────────────────────
const FinanzasCobranzas = () => {
    const [state] = useContext(AppContext);

    const [cobranzas, setCobranzas] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCobranza, setSelectedCobranza] = useState(null);

    // Filtros
    const [periodoFecha, setPeriodoFecha] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [empresaId, setEmpresaId] = useState('');
    const [estadoPago, setEstadoPago] = useState('');
    const [tipoIngreso, setTipoIngreso] = useState('');

    const initialFetchDone = useRef(false);

    useEffect(() => {
        if (!initialFetchDone.current) {
            fetchCobranzas();
            initialFetchDone.current = true;
        }
    }, []);

    // ── Fetch ──────────────────────────────────────────────────────────────────
    const fetchCobranzas = async (filters = {}) => {
        setIsLoading(true);
        try {
            const currentPerfilId = Number(state.user?.perfil_id || state.perfil_id);
            const params = {
                perfil_id: currentPerfilId,
                periodo_fecha: filters.periodo_fecha ?? periodoFecha,
                ...(filters.empresa_id !== undefined ? { empresa_id: filters.empresa_id } : empresaId ? { empresa_id: empresaId } : {}),
                ...(filters.estado_pago !== undefined ? { estado_pago: filters.estado_pago } : estadoPago !== '' ? { estado_pago: estadoPago } : {}),
                ...(filters.tipo_ingreso !== undefined ? { tipo_ingreso: filters.tipo_ingreso } : tipoIngreso ? { tipo_ingreso: tipoIngreso } : {}),
            };
            const res = await cobranzasService.getCobranzas(params);
            setCobranzas(res.data ?? []);
        } catch (error) {
            console.error('Error al obtener cobranzas:', error);
            alert('Error al obtener las cobranzas.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearchClick = () => {
        fetchCobranzas({
            periodo_fecha: periodoFecha,
            empresa_id: empresaId !== '' ? empresaId : undefined,
            estado_pago: estadoPago !== '' ? estadoPago : undefined,
            tipo_ingreso: tipoIngreso !== '' ? tipoIngreso : undefined,
        });
    };

    // ── CRUD ───────────────────────────────────────────────────────────────────
    const handleCreateClick = () => {
        setSelectedCobranza(null);
        setIsModalOpen(true);
    };

    const handleEditClick = (cobranza) => {
        setSelectedCobranza(cobranza);
        setIsModalOpen(true);
    };

    const handleDeleteClick = async (cobranza_id) => {
        if (!window.confirm('¿Está seguro de eliminar esta cobranza?')) return;
        setIsLoading(true);
        try {
            const currentPerfilId = Number(state.user?.perfil_id || state.perfil_id);
            await cobranzasService.deleteCobranza({ cobranza_id, perfil_id: currentPerfilId });
            await fetchCobranzas();
            alert('Cobranza eliminada correctamente.');
        } catch (error) {
            console.error('Error al eliminar cobranza:', error);
            alert('Error al eliminar la cobranza.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async (payload, isEditing) => {
        if (isEditing) {
            await cobranzasService.updateCobranza(payload);
        } else {
            await cobranzasService.insertCobranza(payload);
        }
        await fetchCobranzas();
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedCobranza(null);
    };

    // ── Totales ────────────────────────────────────────────────────────────────
    const totalMonto = cobranzas.reduce((s, c) => s + (parseFloat(c.monto_total) || 0), 0);
    const totalPagado = cobranzas.reduce((s, c) => s + (parseFloat(c.monto_pagado) || 0), 0);
    const totalPendiente = cobranzas.reduce((s, c) => s + (parseFloat(c.monto_pendiente) || 0), 0);

    // ── Exportar Excel ─────────────────────────────────────────────────────────
    const exportarExcel = async () => {
        if (cobranzas.length === 0) { alert('No hay datos para exportar.'); return; }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Cobranzas');

        worksheet.columns = [
            { header: 'N° COBRANZA', key: 'numero_cobranza', width: 18 },
            { header: 'CLIENTE', key: 'cliente', width: 30 },
            { header: 'FECHA EMISIÓN', key: 'fecha_emision', width: 15 },
            { header: 'FECHA VENCIMIENTO', key: 'fecha_vencimiento', width: 18 },
            { header: 'COMPROBANTE', key: 'tipo_comprobante', width: 18 },
            { header: 'SERIE', key: 'serie_comprobante', width: 10 },
            { header: 'NÚMERO', key: 'numero_comprobante', width: 15 },
            { header: 'TIPO INGRESO', key: 'tipo_ingreso', width: 15 },
            { header: 'MONTO TOTAL', key: 'monto_total', width: 14 },
            { header: 'MONTO PAGADO', key: 'monto_pagado', width: 14 },
            { header: 'MONTO PENDIENTE', key: 'monto_pendiente', width: 16 },
            { header: 'DETRACCIÓN', key: 'monto_detraccion', width: 14 },
            { header: 'ESTADO PAGO', key: 'estado_pago', width: 15 },
            { header: 'ESTADO CONT.', key: 'estado_contabilidad', width: 16 },
            { header: 'OBSERVACIÓN', key: 'observacion', width: 30 },
        ];

        cobranzas.forEach(c => {
            worksheet.addRow({
                numero_cobranza: c.numero_cobranza ?? '',
                cliente: c.cliente ?? '',
                fecha_emision: formatDate(c.fecha_emision),
                fecha_vencimiento: formatDate(c.fecha_vencimiento),
                tipo_comprobante: LABEL_TIPO_COMPROBANTE[c.tipo_comprobante] ?? c.tipo_comprobante,
                serie_comprobante: c.serie_comprobante ?? '',
                numero_comprobante: c.numero_comprobante ?? '',
                tipo_ingreso: LABEL_TIPO_INGRESO[c.tipo_ingreso] ?? c.tipo_ingreso,
                monto_total: parseFloat(c.monto_total) || 0,
                monto_pagado: parseFloat(c.monto_pagado) || 0,
                monto_pendiente: parseFloat(c.monto_pendiente) || 0,
                monto_detraccion: parseFloat(c.monto_detraccion) || 0,
                estado_pago: LABEL_ESTADO_PAGO[c.estado_pago] ?? '',
                estado_contabilidad: LABEL_ESTADO_CONT[c.estado_contabilidad] ?? '',
                observacion: c.observacion ?? '',
            });
        });

        // Estilos encabezado
        const headerRow = worksheet.getRow(1);
        headerRow.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
        headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
        headerRow.height = 20;
        headerRow.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F497D' } };
            cell.border = {
                top: { style: 'thin', color: { argb: 'FF000000' } },
                left: { style: 'thin', color: { argb: 'FF000000' } },
                bottom: { style: 'thin', color: { argb: 'FF000000' } },
                right: { style: 'thin', color: { argb: 'FF000000' } },
            };
        });

        // Estilos filas
        const moneyKeys = ['monto_total', 'monto_pagado', 'monto_pendiente', 'monto_detraccion'];
        worksheet.eachRow((row, rowNumber) => {
            if (rowNumber === 1) return;
            if (rowNumber % 2 === 0) {
                row.eachCell(cell => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
                });
            }
            row.eachCell((cell, colNumber) => {
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FF000000' } },
                    left: { style: 'thin', color: { argb: 'FF000000' } },
                    bottom: { style: 'thin', color: { argb: 'FF000000' } },
                    right: { style: 'thin', color: { argb: 'FF000000' } },
                };
                const colKey = worksheet.columns[colNumber - 1]?.key;
                if (moneyKeys.includes(colKey)) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                    cell.numFmt = '"S/ "#,##0.00';
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'left' };
                }
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `CobranzasMec${periodoFecha}.xlsx`);
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    if (isLoading) return <Loader />;

    return (
        <div className="caja-flujo-container">
            <div className="flex items-center justify-between mb-4">
                <h1>FINANZAS — COBRANZAS</h1>
                <ButtonExcel onClick={exportarExcel} disabled={cobranzas.length === 0} />
            </div>

            {/* Filtros */}
            <div className="filtros">
                <div className="campo-fecha">
                    <label htmlFor="periodo_fecha">Período:</label>
                    <input
                        id="periodo_fecha"
                        type="month"
                        value={periodoFecha}
                        onChange={e => setPeriodoFecha(e.target.value)}
                    />
                </div>

                <div className="campo-fecha">
                    <label htmlFor="empresa_id">Empresa ID:</label>
                    <input
                        id="empresa_id"
                        type="number"
                        value={empresaId}
                        onChange={e => setEmpresaId(e.target.value)}
                        placeholder="Todas"
                    />
                </div>

                <div className="campo-fecha">
                    <label htmlFor="estado_pago">Estado Pago:</label>
                    <select
                        id="estado_pago"
                        value={estadoPago}
                        onChange={e => setEstadoPago(e.target.value)}
                    >
                        <option value="">Todos</option>
                        <option value="0">Pendiente</option>
                        <option value="1">Pago Parcial</option>
                        <option value="2">Pagado</option>
                        <option value="3">Anulado</option>
                    </select>
                </div>

                <div className="campo-fecha">
                    <label htmlFor="tipo_ingreso">Tipo Ingreso:</label>
                    <select
                        id="tipo_ingreso"
                        value={tipoIngreso}
                        onChange={e => setTipoIngreso(e.target.value)}
                    >
                        <option value="">Todos</option>
                        <option value="1">Servicio</option>
                        <option value="2">Producto</option>
                        <option value="3">Alquiler</option>
                        <option value="4">Consultoría</option>
                        <option value="5">Otro</option>
                    </select>
                </div>
            </div>

            <ButtonSearch onClick={handleSearchClick} isLoading={isLoading} />

            {/* Resumen */}
            <div style={{
                marginTop: '15px',
                padding: '10px 15px',
                borderRadius: '8px',
                backgroundColor: '#f8f8f8',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                display: 'flex',
                gap: '24px',
                flexWrap: 'wrap',
                width: 'fit-content'
            }}>
                <p style={{ color: 'green', fontSize: '1em', fontWeight: 'bold', margin: 0 }}>
                    N° de registros: {cobranzas.length}
                </p>
                <p style={{ color: '#333', fontSize: '1em', fontWeight: 'normal', margin: 0 }}>
                    Total: <strong>S/ {totalMonto.toFixed(2)}</strong>
                </p>
                <p style={{ color: '#155724', fontSize: '1em', fontWeight: 'normal', margin: 0 }}>
                    Cobrado: <strong>S/ {totalPagado.toFixed(2)}</strong>
                </p>
                <p style={{ color: '#856404', fontSize: '1em', fontWeight: 'normal', margin: 0 }}>
                    Pendiente: <strong>S/ {totalPendiente.toFixed(2)}</strong>
                </p>
            </div>

            {/* Botón nuevo */}
            <div className="nuevo-movimiento-section">
                <ButtonInsert onClick={handleCreateClick} />
            </div>

            {/* Tabla */}
            <div className="table-container">
                <Table>
                    <thead>
                        <tr>
                            <th>N° COBRANZA</th>
                            <th>CLIENTE</th>
                            <th>FECHA EMISIÓN</th>
                            <th>FECHA VENCIMIENTO</th>
                            <th>COMPROBANTE</th>
                            <th>SERIE / NÚM.</th>
                            <th>TIPO INGRESO</th>
                            <th>MONTO TOTAL</th>
                            <th>MONTO PAGADO</th>
                            <th>MONTO PENDIENTE</th>
                            <th>ESTADO PAGO</th>
                            <th>ESTADO CONT.</th>
                            <th>ACCIONES</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cobranzas.length > 0 ? (
                            cobranzas.map(c => (
                                <tr key={c.cobranza_id}>
                                    <td>{c.numero_cobranza ?? '—'}</td>
                                    <td>{c.cliente ?? '—'}</td>
                                    <td>{formatDate(c.fecha_emision)}</td>
                                    <td>{formatDate(c.fecha_vencimiento)}</td>
                                    <td>{LABEL_TIPO_COMPROBANTE[c.tipo_comprobante] ?? '—'}</td>
                                    <td>{[c.serie_comprobante, c.numero_comprobante].filter(Boolean).join(' / ') || '—'}</td>
                                    <td>{LABEL_TIPO_INGRESO[c.tipo_ingreso] ?? '—'}</td>
                                    <td>{formatMonto(c.monto_total)}</td>
                                    <td>{formatMonto(c.monto_pagado)}</td>
                                    <td>{formatMonto(c.monto_pendiente)}</td>
                                    <td>
                                        <span style={{
                                            padding: '3px 10px',
                                            borderRadius: '12px',
                                            fontSize: '0.82em',
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                            ...COLOR_ESTADO_PAGO[c.estado_pago]
                                        }}>
                                            {LABEL_ESTADO_PAGO[c.estado_pago] ?? '—'}
                                        </span>
                                    </td>
                                    <td>{LABEL_ESTADO_CONT[c.estado_contabilidad] ?? '—'}</td>
                                    <td>
                                        <div className="acciones-container">
                                            <ButtonUpdate onClick={() => handleEditClick(c)} />
                                            <ButtonDelete onClick={() => handleDeleteClick(c.cobranza_id)} />
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={13} className="text-center text-gray-500 py-4">
                                    No hay cobranzas registradas para el período seleccionado.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <ModalFinanzasCobranzas
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                cobranza={selectedCobranza}
                onSave={handleSave}
            />
        </div>
    );
};

export default FinanzasCobranzas;